import type {
  FishCropImageStatus,
  LiceFilterClass,
  WelfareFilterClass,
} from '@restDataAccess/orbitOneGateway';

import {
  type FishWelfareResult,
  type FishLiceResult,
  type WelfareFishCandidate,
  WelfareType,
} from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';
import { toCamelCase } from 'remeda';

import {
  liceFilterClasses,
  observationBreakdownTranslationKeys,
  welfareFilterClasses,
  type ObservationBreakdownEntry,
  type ObservationBreakdownCategory,
  fishWelfareResponseMapping,
  singleCategoryWelfareTypes,
} from './fishObservationGallery.constants.ts';
import {
  type BoundingBox,
  type DisplayCamera,
  type CameraFilterType,
  type FishObservationSlide,
  type LocalizedObservationBreakdownEntry,
  type OrbitOneGalleryFilterClass,
  FishWelfareResponse,
} from './fishObservationGallery.types.ts';

/** A single observation paired with the crop path chosen for display. */
type FishGalleryItem = {
  observation: WelfareFishCandidate;
  path: string;
};

/** An observation and the camera filter that decides which of its crops is displayed. */
type ObservationCameraArgs = {
  observation: WelfareFishCandidate;
  cameraFilterType: CameraFilterType;
};

type ToGalleryItemsArgs = {
  observations: WelfareFishCandidate[];
  cameraFilterType: CameraFilterType;
};

/** Observations that have a usable crop path, in the order returned by the gateway. */
export const toGalleryItems = ({
  observations,
  cameraFilterType,
}: ToGalleryItemsArgs): FishGalleryItem[] =>
  observations.flatMap(observation => {
    const path = resolveCropPath({ observation, cameraFilterType });
    return path ? [{ observation, path }] : [];
  });

/** Zero-padded image index for thumbnail alt text (e.g. "Fish observation 001"). */
export const padImageNumber = (oneBasedIndex: number): string =>
  String(oneBasedIndex).padStart(3, '0');

type NextBackgroundImagePathsArgs = {
  allPaths: string[];
  queuedPaths: string[];
  statusByPath: Record<string, FishCropImageStatus>;
  thumbnailsReady: boolean;
  batchSize?: number;
};

/**
 * Adds the next full-resolution prefetch batch after the current batch has settled.
 */
export const nextBackgroundImagePaths = ({
  allPaths,
  queuedPaths,
  statusByPath,
  thumbnailsReady,
  batchSize = 2,
}: NextBackgroundImagePathsArgs): string[] => {
  const availablePaths = new Set(allPaths);
  const currentPaths = queuedPaths.filter(path => availablePaths.has(path));
  const queueWasFiltered = currentPaths.length !== queuedPaths.length;

  if (
    !thumbnailsReady ||
    currentPaths.some(path => (statusByPath[path] ?? 'loading') === 'loading')
  ) {
    return queueWasFiltered ? currentPaths : queuedPaths;
  }

  const queued = new Set(currentPaths);
  const nextPaths = allPaths.filter(path => !queued.has(path)).slice(0, batchSize);
  if (nextPaths.length === 0) return queueWasFiltered ? currentPaths : queuedPaths;
  return [...currentPaths, ...nextPaths];
};

type ResolveImageStatusArgs = {
  source: string | undefined;
  status: FishCropImageStatus | undefined;
};

/** Grid cards stay idle once any image is available, including a thumb after a full-res failure. */
export const resolveImageStatus = ({
  source,
  status,
}: ResolveImageStatusArgs): FishCropImageStatus => (source ? 'idle' : (status ?? 'loading'));

type ResolveFullResolutionStateArgs = {
  fullResolutionSource: string | undefined;
  fullResolutionStatus: FishCropImageStatus | undefined;
};

export const resolveFullResolutionState = ({
  fullResolutionSource,
  fullResolutionStatus,
}: ResolveFullResolutionStateArgs): FishObservationSlide['fullResolutionState'] => {
  if (fullResolutionSource) return 'ready';
  if (fullResolutionStatus === 'error') return 'error';
  return 'preview';
};

type FormatFilterLabelsArgs = {
  filters: OrbitOneGalleryFilterClass[];
  labelForClass: (_class: OrbitOneGalleryFilterClass) => string;
};

/** Comma-separated, alphabetically sorted labels for the active filter classes. */
export const formatFilterLabels = ({ filters, labelForClass }: FormatFilterLabelsArgs): string =>
  [...filters]
    .map(labelForClass)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

type FormatCapturedArgs = {
  recordedAt: Date | string;
  language: string;
};

/** Localized capture timestamp as `date - time` for the image caption. */
export const formatCaptured = ({ recordedAt, language }: FormatCapturedArgs): string => {
  const dateTime = (
    typeof recordedAt === 'string' ? DateTime.fromISO(recordedAt) : DateTime.fromJSDate(recordedAt)
  ).setLocale(language);
  return `${dateTime.toLocaleString(DateTime.DATE_SHORT)} - ${dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)}`;
};

type CountObservationByCategoryArgs = {
  observation: WelfareFishCandidate;
  availableFilterClasses?: OrbitOneGalleryFilterClass[];
};

/**
 * Observation detections grouped into caption categories, in caption order. Welfare results with a
 * score are counted per score, lowest first; lice results are not (their `score` is confidence).
 */
const countObservationByCategory = ({
  observation,
  availableFilterClasses,
}: CountObservationByCategoryArgs): ObservationBreakdownEntry[] => {
  const entries = new Map<string, ObservationBreakdownEntry>();
  const add = ({ category, score }: { category: ObservationBreakdownCategory; score?: number }) => {
    const key = `${category}:${score ?? ''}`;
    const entry = entries.get(key);
    if (entry) entry.count += 1;
    else entries.set(key, { category, score, count: 1 });
  };
  const addWelfareResults = ({
    category,
    results,
  }: {
    category: ObservationBreakdownCategory;
    results: FishWelfareResult[];
  }) => {
    const byScore = [...results].sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity));
    for (const { score } of byScore) add({ category, score });
  };
  // No active filter means every class is shown.
  const isShown = (filterClass: OrbitOneGalleryFilterClass | undefined) =>
    !availableFilterClasses?.length ||
    (filterClass !== undefined && availableFilterClasses.includes(filterClass));

  // Lice results carry snake_case response types; females with and without egg strings are
  // separate categories so they match the differently coloured boxes.
  for (const { type } of annotationResultsFor({
    observation,
    annotationType: 'lice',
  })) {
    const responseType = type as FishWelfareResponse;
    if (!isShown(fishWelfareResponseMapping[responseType])) continue;
    add({ category: responseType });
  }

  // Wound results are split into active and healed by their result type.
  if (isShown(WelfareType.Wounds)) {
    const woundResults = observation.welfare.wounds?.result ?? [];
    addWelfareResults({
      category: FishWelfareResponse.ActiveWound,
      results: woundResults.filter(({ type }) => type === FishWelfareResponse.ActiveWound),
    });
    addWelfareResults({
      category: FishWelfareResponse.HealedWound,
      results: woundResults.filter(({ type }) => type === FishWelfareResponse.HealedWound),
    });
  }

  // Remaining welfare types count every result under a single category.
  for (const { welfareType, welfareKey, category } of singleCategoryWelfareTypes) {
    if (isShown(welfareType)) {
      addWelfareResults({
        category,
        results: observation.welfare[welfareKey]?.result ?? [],
      });
    }
  }

  return [...entries.values()];
};

type ObservationBreakdownTranslator = (
  key: (typeof observationBreakdownTranslationKeys)[ObservationBreakdownCategory],
  options: { count: number }
) => string;

type LocalizeObservationBreakdownArgs = CountObservationByCategoryArgs & {
  t: ObservationBreakdownTranslator;
};

/** Localized observation breakdown entries via `orbitOne` i18n keys; empty when no detections. */
export const localizeObservationBreakdown = ({
  observation,
  t,
  availableFilterClasses,
}: LocalizeObservationBreakdownArgs): LocalizedObservationBreakdownEntry[] =>
  countObservationByCategory({ observation, availableFilterClasses }).map(
    ({ category, score, count }) => ({
      id: `${category}:${score ?? ''}`,
      label: t(observationBreakdownTranslationKeys[category], { count }),
      category,
      score,
    })
  );

type UnassessedFilterClassesArgs = {
  observation: WelfareFishCandidate;
  filterClasses: OrbitOneGalleryFilterClass[];
};

/**
 * Filter classes the image was not assessed for. Images are sampled per welfare type: a packet
 * with an empty `result` means assessed with no detections, a missing packet means not assessed.
 */
export const unassessedFilterClasses = ({
  observation,
  filterClasses,
}: UnassessedFilterClassesArgs): OrbitOneGalleryFilterClass[] =>
  filterClasses.filter(filterClass => {
    const welfareKey = isLiceFilterClass(filterClass) ? 'lice' : toCamelCase(filterClass);
    return observation.welfare[welfareKey]?.result == null;
  });

type FormatMeasurementArgs = {
  value: number;
  unit: 'meter' | 'kilogram';
  language: string;
};

/** Localized measurement with its unit (e.g. "5.6 m", "4.986 kg"). */
export const formatMeasurement = ({ value, unit, language }: FormatMeasurementArgs): string =>
  new Intl.NumberFormat(language, {
    style: 'unit',
    unit,
    maximumFractionDigits: unit === 'kilogram' ? 3 : 1,
  }).format(value);

/** Resolve which camera's crop is displayed for an observation. */
const resolveDisplayCamera = ({
  observation,
  cameraFilterType,
}: ObservationCameraArgs): DisplayCamera => {
  if (cameraFilterType === 'welfare') {
    return (observation.welfareCamera ?? observation.liceCamera ?? 'primary') as DisplayCamera;
  }
  return (observation.liceCamera ?? observation.welfareCamera ?? 'primary') as DisplayCamera;
};

/**
 * Camera whose image is actually loaded. Matches {@link resolveCropPath}: when the preferred camera
 * has no path, the gallery falls back to `images.primary`, so crop size and lice boxes must use
 * `primary` too.
 */
const resolveLoadedCamera = ({
  observation,
  cameraFilterType,
}: ObservationCameraArgs): DisplayCamera => {
  const preferred = resolveDisplayCamera({ observation, cameraFilterType });
  return observation.images[preferred] != null ? preferred : 'primary';
};

/**
 * Pick the crop to show for an observation: the lice camera when present, otherwise the welfare
 * camera, falling back to the primary crop. Returns `undefined` when no crop path exists.
 */
const resolveCropPath = ({
  observation,
  cameraFilterType,
}: ObservationCameraArgs): string | undefined => {
  const camera = resolveLoadedCamera({ observation, cameraFilterType });
  return observation.images[camera];
};

/**
 * Pixel dimensions of the displayed crop from observation metadata. `crop` is `[x0, y0, x1, y1]` in
 * full-frame pixels, so the crop's own size is the box width/height — this equals the crop image's
 * intrinsic size and is what the lightbox needs as the slide's `width`/`height` so zoom pan bounds
 * match the rendered image. Returns `undefined` when per-camera crop metadata is missing.
 * Do not use {@link WelfareFishCandidate.resolution} — that is the full frame, not the crop.
 *
 * Note: the lice overlay does not use this — it reads the loaded image's intrinsic size directly,
 * which is always the true crop-pixel space. This is only the pre-load hint for zoom pan bounds.
 */
export const resolveCropPixelSize = ({
  observation,
  cameraFilterType,
}: ObservationCameraArgs): [number, number] | undefined => {
  const crop = observation.crop?.[resolveLoadedCamera({ observation, cameraFilterType })];
  if (crop && crop.length === 4) {
    const width = Math.round(crop[2] - crop[0]);
    const height = Math.round(crop[3] - crop[1]);
    if (width > 0 && height > 0) return [width, height];
  }
  return undefined;
};

const isBoundingBox = (value: unknown): value is BoundingBox =>
  Array.isArray(value) && value.length === 4 && value.every(n => typeof n === 'number');

type ExtractBboxArgs = {
  detection: FishLiceResult | FishWelfareResult;
  camera: DisplayCamera;
};

/**
 * Extract a detection's bbox for a given camera. The generated {@link LiceResult} carries a
 * per-camera `primaryDetection` / `secondaryDetection`, each with its own `bbox`. Returns
 * `undefined` when the detection has no box for this camera.
 */
const extractBbox = ({ detection, camera }: ExtractBboxArgs): BoundingBox | undefined => {
  const box =
    camera === 'primary' ? detection.primaryDetection?.bbox : detection.secondaryDetection?.bbox;
  return isBoundingBox(box) ? box : undefined;
};

type ResolveAnnotationBoxesArgs = {
  observation: WelfareFishCandidate;
  annotationTypes: OrbitOneGalleryFilterClass[];
};

/**
 * Lice boxes for the crop shown in the lightbox. Only detections with a box on the displayed
 * camera are returned; detections annotated solely on the other camera are skipped because their
 * coordinates do not match this crop. Boxes are in the crop's original-crop pixel space (the
 * loaded image's intrinsic size), not {@link WelfareFishCandidate.resolution}.
 */
export const resolveAnnotationBoxes = ({
  observation,
  annotationTypes,
}: ResolveAnnotationBoxesArgs) => {
  const selectedLiceTypes = annotationTypes.filter(isLiceFilterClass);
  const selectedWelfareTypes = annotationTypes.filter(isWelfareFilterClass);

  const cameraFilterType = selectedWelfareTypes?.length > 0 ? 'welfare' : 'lice';

  const observationResultTypes: (keyof WelfareFishCandidate['welfare'])[] = [
    ...(selectedLiceTypes.length ? ([toCamelCase(WelfareType.Lice)] as const) : []),
    ...selectedWelfareTypes.map(type => toCamelCase(type)),
  ];

  const camera = resolveLoadedCamera({ observation, cameraFilterType });
  const results = observationResultTypes.flatMap(annotationType =>
    annotationResultsFor({ observation, annotationType })
  );

  return results.flatMap(detection => {
    const responseFilterType = fishWelfareResponseMapping[detection.type as FishWelfareResponse];
    const isLice = isLiceFilterClass(responseFilterType as OrbitOneGalleryFilterClass);
    if (isLice && !selectedLiceTypes.includes(responseFilterType as LiceFilterClass)) return [];
    const bbox = extractBbox({ detection, camera });
    if (!bbox) return [];
    return [{ bbox, type: detection.type as FishWelfareResponse }];
  });
};

type AnnotationResultsForArgs = {
  observation: WelfareFishCandidate;
  annotationType: keyof WelfareFishCandidate['welfare'];
};

const annotationResultsFor = ({
  observation,
  annotationType,
}: AnnotationResultsForArgs): (FishLiceResult | FishWelfareResult)[] => {
  return observation.welfare[annotationType]?.result ?? [];
};

export const isLiceFilterClass = (
  annotationType: OrbitOneGalleryFilterClass
): annotationType is LiceFilterClass =>
  liceFilterClasses.includes(annotationType as LiceFilterClass);

export const isWelfareFilterClass = (
  annotationType: OrbitOneGalleryFilterClass
): annotationType is WelfareFilterClass =>
  welfareFilterClasses.includes(annotationType as WelfareFilterClass);

type FilterLiceGalleryObservationsArgs = {
  observations: WelfareFishCandidate[];
  filters: OrbitOneGalleryFilterClass[];
};

/**
 * Narrows the lice gallery's fish to those matching a selected chip. The gateway returns every fish
 * with a scale loss result when the scale loss chip is selected, so "scale loss" is narrowed here to
 * a score above 0. No selected chips means no filter.
 */
export const filterLiceGalleryObservations = ({
  observations,
  filters,
}: FilterLiceGalleryObservationsArgs): WelfareFishCandidate[] =>
  filters.length === 0
    ? observations
    : observations.filter(observation =>
        filters.some(filterClass => {
          if (isLiceFilterClass(filterClass)) {
            return annotationResultsFor({ observation, annotationType: 'lice' }).some(
              ({ type }) => fishWelfareResponseMapping[type as FishWelfareResponse] === filterClass
            );
          }
          return (
            filterClass === WelfareType.Scaleloss &&
            (observation.welfare.scaleloss?.result ?? []).some(({ score }) => (score ?? 0) > 0)
          );
        })
      );

type FilterClassLabelKeyArgs = {
  filterClass: OrbitOneGalleryFilterClass;
  availableFilterClasses: OrbitOneGalleryFilterClass[];
};

/**
 * i18n key of a filter class's label. In the lice gallery the scale loss chip only selects fish with
 * a scale loss score above 0, so it gets its own label.
 */
export const filterClassLabelKey = ({
  filterClass,
  availableFilterClasses,
}: FilterClassLabelKeyArgs) =>
  filterClass === WelfareType.Scaleloss && availableFilterClasses.some(isLiceFilterClass)
    ? ('liceCount.imageGallery.scaleLossPositive' as const)
    : (`liceCount.imageGallery.filterClasses.${filterClass}` as const);
